import { Component, Input, OnInit, Output, EventEmitter, inject } from '@angular/core';
import { AppService } from '../../services';
import { Message, SignalRService } from '../../../assets/signalr.service';
import { AppStateHolder } from '../../shared';
import { Router } from '@angular/router';
import { apps } from '../../constants';
import { MessageService, PrimeTemplate } from 'primeng/api';
import { AppProperty } from '../../properties';
import { SidebarModule } from 'primeng/sidebar';
import { ToastModule } from 'primeng/toast';
import { TranslateModule } from '@ngx-translate/core';
import { DebugLoggerService } from '../../services/debug-logger.service';

@Component({
  selector: 'alert',
  templateUrl: './alert.component.html',
  styleUrls: ['./alert.component.scss'],
  standalone: true,
  imports: [SidebarModule, ToastModule, PrimeTemplate, TranslateModule],
})
export class AlertComponent implements OnInit {
  private _appService = inject(AppService);
  private _signalRService = inject(SignalRService);
  appStateHolder = inject(AppStateHolder);
  private _router = inject(Router);
  private _messageService = inject(MessageService);
  private _appProperty = inject(AppProperty);
  private _debugLogger = inject(DebugLoggerService);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);


  constructor() { }

  alerts: Message[];
  newMessages: number;
  alertMessage: any;

  @Input() visible: boolean = false;
  @Input() message: boolean = false;

  @Output() close: EventEmitter<boolean> = new EventEmitter();
  @Output() alertsCount: EventEmitter<number> = new EventEmitter();

  /** Mobile: drawer dal basso (bottom sheet); desktop: laterale destro. */
  get drawerPosition(): 'right' | 'bottom' {
    return window.innerWidth <= 640 ? 'bottom' : 'right';
  }

  async ngOnInit() {
    this._debugLogger.logLifecycle('Alert', 'ngOnInit');
    this.getUserAlerts();

    this._signalRService.sub().subscribe({
      next: m => {
        this._debugLogger.logEvent('Alert', 'SignalR message received', { id: m.Id, type: m.Message?.Type, subject: m.Message?.Subject });
        let message = new Message(null);

        message.Id = m.Id;
        message.IsNew = (m.Status === 0);
        message.AppId = m.Message.AppId;
        message.Text = m.Message.Text;
        message.Type = m.Message.Type;
        message.Subject = m.Message.Subject;
        message.ObjectId = m.Message.ObjectId;

        this.pushMessage(message);
        this.onNotification(message);

        this.setAlertCount();
      }
    });
  }

  /**
   * Show toast for new alert
   * @param message SignalR message
   */
  onNotification(message: Message): void {
    this._appProperty.setNewAlertReceived(true);
    this.alertMessage = message;
    this._messageService.add({ key: 'newAlert', severity: 'info', summary: message.Subject, detail: `${message.Text}` });
  }

  /**
   * Get user alert and map on Message class
   */
  getUserAlerts() {
    this.alerts = new Array<Message>();

    this._appService.getUserAlerts().subscribe({
      next: items => {
        this._debugLogger.logAction('Alert', 'alerts loaded', { count: items?.length });
        let order = items ? items.length : 0;

        items.forEach(i => {
          let message = new Message(i);
          message.Order = order;

          this.alerts.push(message);
          order--;
        });

        this.setAlertCount();
      }
    });
  }

  /**
   * Navigates to the context specified by the alert.
   * Updates the alert as read and emits a close event.
   * Sets the isNew flag of the alert to false.
   * Sets the app navbar class and navigates to the appropriate URL based on the alert type and object ID.
   * @param alert The alert message object.
   */
  goToContext(alert: Message): void {
    this._debugLogger.logAction('Alert', 'goToContext', { alertId: alert.Id, type: alert.Type, objectId: alert.ObjectId });
    this._appService.updateAsRead(alert.Id).subscribe();
    this.close.emit(true)

    alert.IsNew = false;
    let url = this.appStateHolder.getAppUrl(alert.AppId.toString());
    this.appStateHolder.setAppNavbarClass(alert.AppId.toString());

    if (url.length == 0) {
      this._router.navigate(['/app', alert.AppId]);
      return;
    }
    let link;

    if (alert.Type == 1)
      link = ['/app', `${url}/dialog/${alert.ObjectId}`];

    if (alert.Type == 2)
      link = ['/app', `${url}/activity/${alert.ObjectId}`];

    if (alert.Type == 3)
      link = ['/app', `${url}/project/${alert.ObjectId}`];

    this._debugLogger.logNavigation(link?.join('/'));
    this._router.navigate(link);
  }


  /**
   * Pushes a message to the alerts array and sorts the array based on the message order.
   * @param message The message to be pushed to the alerts array.
   */
  private pushMessage(message: Message) {
    if (this.alerts === undefined)
      this.alerts = new Array<Message>();

    this.alerts.push(message);
    message.Order = this.alerts ? this.alerts.length : 0;

    this.alerts.sort((a: Message, b: Message) => {
      if (a.Order < b.Order) {
        return 1;
      } else if (a.Order > b.Order) {
        return -1;
      } else {
        return 0;
      }
    });
  }

  /**
   * Remove all alerts
   */
  clearAlers() {
    this._debugLogger.logAction('Alert', 'clearAllAlerts', { count: this.alerts?.length });
    for (const alert of this.alerts) {
      this.removeAlert(alert);
    }
  }

  /**
   * Remove alert from object and user alerts (backend)
   * @param alert SignalRMessage
   */
  removeAlert(alert: Message) {
    this._debugLogger.logAction('Alert', 'removeAlert', { alertId: alert.Id });
    this._appService
      .deleteAlert(alert.Id).subscribe({
        complete: () => {
          let index = this.alerts.indexOf(alert);
          if (index !== -1) {
            this.alerts.splice(index, 1);
            this.setAlertCount();
          }
        }
      });
  }


  setAlertCount() {
    let notReadedAlerts = this.alerts.filter(a => { return a.IsNew === true })
    this._debugLogger.logEvent('Alert', 'alertsCount emitted', notReadedAlerts.length);
    this.alertsCount.emit(notReadedAlerts.length);
  }

  closeSidebar() {
    this.close.emit(true);
  }
}
